import { Component, OnDestroy, OnInit } from '@angular/core';
import { PaginatedResponse } from '../../../common/models/common.model';
import { Router } from '@angular/router';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { Order, OrderItemStatus, OrderTypeTexts } from '../../orders.models';
import { OrdersService } from '../../orders.service';
import { NO_ORDERS_ALERT_MSG } from '../../../common/helpers/constants';
import { EmployeesService } from '../../../employees/employees.service';
import { EmployeeBasicInfo } from '../../../employees/employees.model';
import { DealersService } from '../../../dealers/dealers.service';
import { Dealer } from '../../../dealers/dealers.model';
import { CompanyFactoryService } from '../../../production-inventory/services/company-factory.service';
import { Company } from '../../../production-inventory/production-inventory.model';
import { SnackbarService } from '../../../services/snackbar.service';
import { SnackbarAlert, SnackbarAlertStatus } from '../../../utils';

@Component({
  selector: 'app-orders-list',
  standalone: false,
  templateUrl: './orders-list.component.html',
  styleUrl: './orders-list.component.scss',
})
export class OrdersListComponent implements OnInit, OnDestroy {
  protected isDataLoading: boolean = false;
  protected isExporting: boolean = false;
  protected orders: Order[] = [];

  hasNextPage: boolean = false;
  hasPreviousPage: boolean = false;
  pageSize: number = 10;
  totalCount: number = 0;

  protected currentPage: number = 1;
  protected selectedOrderStatus: string | null = null;
  protected employeesList: EmployeeBasicInfo[] = [];
  protected selectedEmployee: string | null = null;
  protected selectedDealer: string | null = null;
  protected dealersList: Dealer[] = [];
  protected selectedCompanyId: string | null = null;
  protected companiesList: Company[] = [];

  // 'YYYY-MM-DD', the format both <input type="date"> and the API use.
  protected fromDate: string | null = null;
  protected toDate: string | null = null;

  protected readonly orderStatusOptions = [
    { label: 'Pending', value: OrderItemStatus.PENDING },
    { label: 'Delivered', value: OrderItemStatus.DELIVERED },
    { label: 'Partially Delivered', value: OrderItemStatus.PARTIALLY_DELIVERED },
    { label: 'Dispatched', value: OrderItemStatus.DISPATCHED },
    { label: 'Partially Dispatched', value: OrderItemStatus.PARTIALLY_DISPATCHED },
    { label: 'Cancelled', value: OrderItemStatus.CANCELLED },
  ];

  private employeesSubscription!: Subscription;

  protected readonly NO_ORDERS_ALERT_MSG = NO_ORDERS_ALERT_MSG;
  protected readonly OrderTypeTexts = OrderTypeTexts;

  constructor(
    private employeesService: EmployeesService,
    private dealersService: DealersService,
    private ordersService: OrdersService,
    private companyFactoryService: CompanyFactoryService,
    protected router: Router,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.isDataLoading = true;

    const employees$ = this.employeesService.lighter_list({ team: 'sales' });
    const dealers$ = this.dealersService.lighter_list();
    const companies$ = this.companyFactoryService.listCompanies();

    this.employeesSubscription = forkJoin([employees$, dealers$, companies$]).subscribe({
      next: ([employeeList, dealersList, companiesList]) => {
        this.employeesList = employeeList;
        this.dealersList = dealersList;
        this.companiesList = companiesList;
        this.loadOrders();
      },
    });
  }

  viewOrderDetails(orderId: string): void {
    void this.router.navigate(['/orders', orderId]);
  }

  protected loadOrders(): void {
    this.isDataLoading = true;

    const params = {
      page: this.currentPage,
      status: this.selectedOrderStatus,
      employee: this.selectedEmployee,
      dealer: this.selectedDealer,
      company: this.selectedCompanyId,
      from_date: this.fromDate,
      to_date: this.toDate,
    };

    this.ordersService
      .get_orders(params)
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (order: PaginatedResponse<Order>) => {
          this.orders = order.results;
          this.hasNextPage = !!order.next;
          this.hasPreviousPage = !!order.previous;
          this.totalCount = order.count;
        },
      });
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.loadOrders();
  }

  /** The API answers a reversed range with a 400, so hold the request and say so on screen. */
  protected get isRangeReversed(): boolean {
    return !!this.fromDate && !!this.toDate && this.fromDate > this.toDate;
  }

  protected onDateRangeChange(): void {
    if (this.isRangeReversed) return;

    // A newly filtered result set may not have the page the user is currently on.
    this.currentPage = 1;
    this.loadOrders();
  }

  protected exportTotal(): void {
    this.isExporting = true;

    const params: any = {};
    if (this.selectedOrderStatus) params.status = this.selectedOrderStatus;
    if (this.selectedEmployee) params.employee = this.selectedEmployee;
    if (this.selectedDealer) params.dealer = this.selectedDealer;
    if (this.selectedCompanyId) params.company = this.selectedCompanyId;
    if (this.fromDate) params.from_date = this.fromDate;
    if (this.toDate) params.to_date = this.toDate;

    this.ordersService
      .exportTotal(params)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: response => {
          const blob = response.body!;
          const disposition = response.headers.get('Content-Disposition') ?? '';
          const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          const filename =
            match?.[1]?.replace(/['"]/g, '') ||
            `Total_Orders_Report_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xlsx`;

          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.snackbarService.show(SnackbarAlert.SOMETHING_WENT_WRONG, SnackbarAlertStatus.ERROR);
        },
      });
  }

  ngOnDestroy(): void {
    this.employeesSubscription?.unsubscribe();
  }
}
